import { Outlet } from 'react-router-dom';

import { MembershipNotice } from '../features/onboarding/MembershipGate';


import type { Actor } from '../lib/auth';
import { BackToHq } from './CommissionerShell';
import type { NavKey } from '@league/shared/contract';
import { useCapabilities } from '../lib/capabilities';
import { Rail, type RailGroup, type RailItem, UserChip } from './Rail';

import { KIT_IN_V1 } from '@league/shared/features';
import { PageErrorBoundary } from '../components/PageErrorBoundary';

/**
 * The director's app. A flat rail — everything a director does is one of these seven things, and
 * grouping seven items invents a hierarchy that isn't there.
 *
 * "My leagues" does NOT expand into a list of leagues here yet. The spec asks for that, and it is
 * right once a director runs several at once; with one or two it is a disclosure triangle that
 * reveals a list identical to the page it links to. It goes in with the league workspace.
 */
/*
  Each item with the key `/api/me` names it by (2026-10-16): the rail shows only the keys the server
  sends, so an instructor sees Dashboard, The Playbook and Questions & Support, and a director sees
  everything, Instructors included.
*/
const ITEMS: (RailItem & { key: NavKey })[] = [
  { key: 'dashboard', to: '/director', label: 'Dashboard', icon: 'dashboard', end: true },
  { key: 'leagues', to: '/director/leagues', label: 'My Leagues', icon: 'league' },
  { key: 'players', to: '/director/players', label: 'Players', icon: 'players' },
  /* Kit is not part of V1 — see `features.ts`. Filtered below rather than deleted. */
  ...(KIT_IN_V1 ? [{ key: 'kit' as const, to: '/director/kit', label: 'Kit & tiles', icon: 'kit' as const }] : []),
  { key: 'playbook', to: '/director/playbook', label: 'The Playbook', icon: 'playbook' },
  /*
    The notes thread is the support channel: a director starts it, the commissioner replies.
    Relabelled rather than replaced — the route, the screen and its data are unchanged.

    Tasks and incidents are no longer in the rail. Both routes and both screens still exist and
    still work; only their way in from here was removed.
  */
  { key: 'notes', to: '/director/notes', label: 'Questions & Support', icon: 'notes' },
  /* Who runs her nights (2026-10-16). */
  { key: 'instructors', to: '/director/instructors', label: 'Instructors', icon: 'players' },
  { key: 'settings', to: '/director/settings', label: 'Settings', icon: 'settings' },
];

export function DirectorShell({
  actor,
  brand,
  onSignOut,
  playsToo = false,
}: {
  actor: Actor;
  brand: string;
  onSignOut: () => void;
  /** She also plays — the player screens are one item away (owner, 2026-09-26). */
  playsToo?: boolean;
}) {
  /*
    "MY GAMES" — for a director who also plays, on the same email and the same login. Her player
    screens (Today, Standings, You) are the same ones every player gets; this is only the way in.
    Absent for a director with no player record, who has nothing there to see.
  */
  const { nav } = useCapabilities();
  const items = ITEMS.filter((item) => nav.includes(item.key)).map(({ key: _key, ...item }) => item);
  const groups: RailGroup[] = playsToo
    ? [{ items }, { items: [{ to: '/my', label: 'My games', icon: 'today' }] }]
    : [{ items }];
  /* With no Settings in her menu, an instructor reaches her own account from her name. */
  const chip = <UserChip name={actor.label} role={actor.role} onSignOut={onSignOut} {...(nav.includes('settings') ? {} : { accountHref: '/director/account' })} />;

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <Rail
        brand={brand}
        groups={groups}
        /** Only the commissioner has somewhere to go back to. */
        header={actor.role === 'commissioner' ? <BackToHq /> : undefined}
        footer={<div className="flex flex-col gap-3">{chip}</div>}
        /* The footer is laptop-only, and on a phone an instructor has no Settings to sign out from (2026-11-01). */
        narrowHeader={nav.includes('settings') ? undefined : chip}
      />

      {/* min-w-0 or a wide table inside makes the whole page scroll sideways instead of the table. */}
      <main className="min-w-0 flex-1">
        {/*
          Getting Started is not rendered here. The onboarding API, its rules and the self-attested
          answers are untouched — only this presentation was removed.
        */}
        <MembershipNotice className="mx-auto max-w-2xl px-5 pt-4" />
        <PageErrorBoundary><Outlet /></PageErrorBoundary>
      </main>
    </div>
  );
}
